import { formatNumber } from "@memorang/helpers/common";
import type { MixedItemTag } from "@memorang/types";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";
import { Text } from "react-native-paper";

import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";

type CheckboxStatus = "checked" | "unchecked" | "indeterminate";

export type NestedTagRowProps = {
	tag: MixedItemTag;
	level: number;
	checked: boolean;
	expanded: boolean;
	hasCheckedDescendant: boolean;
	hasChildren: boolean;
	disableAll?: boolean;
	isDiagnostic?: boolean;
	hasAccess?: boolean;
	maxFilterDepth: number;
	handleSelectCheckbox: (id: string, locked?: boolean) => void;
	setExpanded: (id: number, value: boolean) => void;
};

type ThemeColors = ReturnType<typeof useAppTheme>["colors"];

const ChevronCell = ({
	visible,
	expanded,
	color,
	hoverColor,
	hoverBorderRadius,
	onPress,
}: {
	visible: boolean;
	expanded: boolean;
	color: string;
	hoverColor: string;
	hoverBorderRadius: number;
	onPress: () => void;
}) => {
	if (!visible) {
		return (
			<Box width={24} height={24} justifyContent="center" alignItems="center" />
		);
	}
	return (
		<Pressable
			hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
			onPress={onPress}
			accessibilityRole="button"
			accessibilityLabel={expanded ? "Collapse" : "Expand"}
			style={({
				hovered,
				pressed,
			}: {
				hovered?: boolean;
				pressed?: boolean;
			}) => [
				{
					width: 24,
					height: 24,
					justifyContent: "center",
					alignItems: "center",
				},
				(hovered || pressed) && {
					backgroundColor: hoverColor,
					borderRadius: hoverBorderRadius,
				},
			]}
		>
			<Icon
				name={expanded ? "chevron-down" : "chevron-right"}
				size={18}
				color={color}
			/>
		</Pressable>
	);
};

const LeftSlot = ({
	locked,
	status,
	color,
}: {
	locked?: boolean;
	status: CheckboxStatus;
	color: string;
}) => {
	const iconName =
		status === "checked"
			? "checkbox-marked"
			: status === "indeterminate"
				? "minus-box-outline"
				: "checkbox-blank-outline";

	return (
		<Box
			width={24}
			height={24}
			justifyContent="center"
			alignItems="center"
			pointerEvents="none"
		>
			<Icon name={locked ? "lock-outline" : iconName} size={24} color={color} />
		</Box>
	);
};

const getCheckboxStatus = (
	checked: boolean,
	hasCheckedDescendant: boolean,
): CheckboxStatus => {
	if (checked) {
		return "checked";
	}
	return hasCheckedDescendant ? "indeterminate" : "unchecked";
};

const getControlColor = ({
	colors,
	locked,
	checked,
	hasCheckedDescendant,
}: {
	colors: ThemeColors;
	locked: boolean;
	checked: boolean;
	hasCheckedDescendant: boolean;
}) => {
	if (locked) {
		return colors.onSurfaceVariant;
	}
	return checked || hasCheckedDescendant ? colors.primary : colors.outline;
};

const NestedTagRowImpl = ({
	tag,
	level,
	checked,
	expanded,
	hasCheckedDescendant,
	hasChildren,
	disableAll,
	isDiagnostic,
	hasAccess,
	maxFilterDepth,
	handleSelectCheckbox,
	setExpanded,
}: NestedTagRowProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { isMobile } = useDevice();

	const numItems = tag.numItems || tag.items?.length || 0;
	const locked = !isDiagnostic && Boolean(tag.locked) && hasAccess !== true;
	const disabled = disableAll || numItems === 0;
	const showChevron = hasChildren && !locked;
	const reserveChevronSlot = maxFilterDepth > 1;

	const onPressCheckbox = () => {
		if (disabled) {
			return;
		}
		handleSelectCheckbox(String(tag.id), locked);
	};

	const onPressChevron = () => {
		setExpanded(tag.id, !expanded);
	};

	const indent = level * (isMobile ? 12 : 16);
	const chevronColumnWidth = reserveChevronSlot ? 24 + indent : 0;
	const checkboxStatus = getCheckboxStatus(checked, hasCheckedDescendant);
	const controlColor = getControlColor({
		colors: theme.colors,
		locked,
		checked,
		hasCheckedDescendant,
	});

	return (
		<Pressable
			onPress={onPressCheckbox}
			disabled={disabled}
			accessibilityRole="checkbox"
			accessibilityState={{
				checked: checkboxStatus === "indeterminate" ? "mixed" : checked,
				disabled,
			}}
			style={{
				flexDirection: "row",
				alignItems: "center",
				paddingVertical: 8,
				paddingRight: 0,
				gap: 12,
				opacity: disabled ? 0.5 : 1,
			}}
		>
			{reserveChevronSlot ? (
				<Box width={chevronColumnWidth} alignItems="flex-end">
					<ChevronCell
						visible={showChevron}
						expanded={expanded}
						color={theme.colors.onSurfaceVariant}
						hoverColor={theme.colors.surfaceVariant}
						hoverBorderRadius={theme.roundness * 4}
						onPress={onPressChevron}
					/>
				</Box>
			) : null}
			<LeftSlot locked={locked} status={checkboxStatus} color={controlColor} />
			<Text
				variant="bodyLarge"
				numberOfLines={2}
				style={{
					flex: 1,
					color: theme.colors.onSurface,
				}}
			>
				{tag.title}
			</Text>
			<Box
				paddingHorizontal={8}
				paddingVertical={4}
				borderRadius={theme.roundness * 2}
				backgroundColor={theme.colors.surfaceVariant}
			>
				<Text
					variant="bodySmall"
					style={{ color: theme.colors.onSurface }}
				>{`${formatNumber(numItems)} ${t("common.item", { count: numItems })}`}</Text>
			</Box>
		</Pressable>
	);
};

const NestedTagRow = memo(NestedTagRowImpl);

export default NestedTagRow;
